import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { EdpModalService } from "@edp-components/edp-modal";
import { getUnexpiredContractRequests } from "@edp-store/selectors/dashboard.selectors";
import { GetRequestsAndOperationsDetails, SetRequestsAndOperationsDetails } from "@edp-store/actions/requests-and-operations.actions";
import { CreateRequestModalComponent } from "@edp-pages/requests-and-operations/components/create-request/create-request-modal/create-request-modal.component";
import { StepsService } from "@edp-pages/requests-and-operations/services/steps.service";
import { RequestsAndOperations } from "@edp-models/requests-and-operations";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { LanguageTypeEnum } from "@edp-enums/language.enum";
import { LanguageService } from "@edp-services/language.service";
import { DatalayerService } from "@edp-services/datalayer.service";

export enum RequestStatusDescription {
    CREATED     = "pt.edp.edponline.widget.requests.footer.created",
    INANALYSIS  = "pt.edp.edponline.widget.requests.footer.in-analysis",
    SCHEDULED   = "pt.edp.edponline.widget.requests.footer.scheduled",
    INPROGRESS  = "pt.edp.edponline.widget.requests.footer.in-progress",
    PENDING     = "pt.edp.edponline.widget.requests.footer.pending",
    RESOLVED    = "pt.edp.edponline.widget.requests.footer.resolved",
    MULTIPLE    = "pt.edp.edponline.widget.requests.footer.multiple",
}

@Component({
    selector: "app-edp-widget-requests",
    templateUrl: "./edp-widget-requests.component.html",
    styleUrls: ["./edp-widget-requests.component.scss"]
})
export class EdpWidgetRequestsComponent implements OnInit, OnDestroy {
    public contactInfo: RequestsAndOperations;
    public type: string;
    public motive: string;
    public statusDescription: string;
    public creationDate: string;
    public countRequests: number;
    private unsubscribe = new Subject();

    constructor(
        private readonly store: Store<IAppState>,
        private readonly translate: TranslateService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly stepService: StepsService,
        private readonly edpModalService: EdpModalService,
        private readonly datalayerService: DatalayerService,
        private languageService: LanguageService) {}

    ngOnInit() {
        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getUnexpiredContractRequests)
        ).subscribe((contractRequests: RequestsAndOperations[]) => {
            if (contractRequests) {
                const _activeOnes = contractRequests.filter( req => {
                    switch (req.Status) {
                        case "pt.edp.edponline.support.help.E0001":
                        case "pt.edp.edponline.support.help.E0002":
                        case "pt.edp.edponline.support.help.E0004":
                        case "pt.edp.edponline.support.help.E0005":
                        case "pt.edp.edponline.serviceOrder.status.E0001":
                        case "pt.edp.edponline.serviceOrder.status.E0002":
                        case "pt.edp.edponline.serviceOrder.status.E0003":
                        case "pt.edp.edponline.serviceOrder.status.E0004":
                        case "pt.edp.edponline.serviceOrder.status.E0005":
                            return true;
                        default:
                            return false;
                    }
                });
                if (_activeOnes.length > 1) {
                    this.countRequests = _activeOnes.length;
                    this.statusDescription = this.translate.instant("pt.edp.edponline.widget.requests.footer.multiple");
                } else {
                    this.contactInfo = contractRequests[0];
                    this.statusDescription = this.getFooterDescrition();
                }
            }
        });
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }


    private getFooterDescrition(): string {
        switch (this.contactInfo.Status) {
            case "pt.edp.edponline.support.help.E0001":
            case "pt.edp.edponline.support.help.E0002":
            case "pt.edp.edponline.support.help.E0004":
            case "pt.edp.edponline.serviceOrder.status.E0001":
            case "pt.edp.edponline.serviceOrder.status.E0002":
            case "pt.edp.edponline.serviceOrder.status.E0003":
                return this.translate.instant(RequestStatusDescription.INANALYSIS);
            case "pt.edp.edponline.serviceOrder.status.E0005":
                if (this.contactInfo.Task?.scheduling) {
                    const _date = moment(this.contactInfo.Task?.scheduling.startDate).locale(this.languageService.getLocale()).format("DD MMM");
                    const _startTime = moment(this.contactInfo.Task?.scheduling.startDate).format("HH:mm");
                    const _endTime = moment(this.contactInfo.Task?.scheduling.endDate).format("HH:mm");
                    return this.translate.instant(RequestStatusDescription.SCHEDULED,
                        { date: _date, start: _startTime, end: _endTime});
                }
                break;
            case "pt.edp.edponline.support.help.E0005":
            case "pt.edp.edponline.support.help.E0006":
                return this.translate.instant(RequestStatusDescription.PENDING);
            case "pt.edp.edponline.support.help.E0003":
            case "pt.edp.edponline.support.help.E0007":
            case "pt.edp.edponline.serviceOrder.status.E0006":
            case "pt.edp.edponline.serviceOrder.status.E0007":
            case "pt.edp.edponline.serviceOrder.status.E0008":
            case "pt.edp.edponline.serviceOrder.status.E0009":
            case "pt.edp.edponline.serviceOrder.status.E00010":
            case "pt.edp.edponline.serviceOrder.status.E00011":
                return this.translate.instant(RequestStatusDescription.RESOLVED);
            default:
                return null;
        }
    }

    private datalayerActionRequests(): void {
        this.datalayerService.setGaClick("clickOpenLink", {
            formModal: "/pedidos-apoio",
            interactionName: "pedido",
            interactionLocation: "inicio"
        });
    }

    private datalayerActionRequestDetail(): void {
        this.datalayerService.setGaClick("clickOpenLink", {
            formModal: "/pedidos-apoio/detalhe",
            interactionName: "pedido",
            interactionLocation: "inicio"
        });
    }

    public clickAction(): void {
        const request = this.contactInfo;
        if (request) {
            if (request.DataOrigin === "CS") {
                this.store.dispatch(new SetRequestsAndOperationsDetails(request));
            } else {
                this.store.dispatch(new GetRequestsAndOperationsDetails(request));
            }

            if (request.DataOrigin === "OS" &&
                (request.Status === "pt.edp.edponline.serviceOrder.status.E0002" ||
                request.Status === "pt.edp.edponline.serviceOrder.status.E0003")) {
                this.goToSchedule();
            } else {
                this.datalayerActionRequestDetail();
                this.router.navigate(["/pedidos-apoio/detalhe"], { relativeTo: this.activatedRoute });
            }
        } else {
            this.datalayerActionRequests();
            this.router.navigate(["/pedidos-apoio"], { relativeTo: this.activatedRoute });
        }
    }

    private goToSchedule() {
        this.stepService.setTotalSteps(3);
        this.stepService.setCurrentStep(3);
        this.edpModalService.custom(CreateRequestModalComponent, {
                windowClass: "edp-dialog-fullscreen"
            } as NgbModalOptions, {});
    }
}
